import React, { useState, useEffect, FC } from "react";
import { Link } from "react-router-dom";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  UncontrolledDropdown,
  Badge,
  Col,
  Row,
} from "reactstrap";
import { Icon, Button, TooltipComponent } from "../../../components/Component";
import Swal from "sweetalert2";
import taxMasterApis from "../../../api/master/taxmaster";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import TransactionFlowDefaultTemplate from "../../others/transactionFlowDefaultTemplate";
import dayjs from "dayjs";
import TaxMasterForm from "./TaxMasterForm";
import FormatDate from "../DateFormater";
import { normalizeStatus } from "../../../utils/Utils";

const TaxMaster: FC = () => {
  const [refreshToggle, setRefreshToggle] = useState(false);
  const { data: accessibility } = useSelector(
    (state: any) => state.accessibility
  );
  const [allowedActions, setAllowedActions] = useState<any>({
    add: 0,
    view: 0,
    edit: 0,
    approve: 0,
    reject: 0,
    cancel: 0,
  });

  // Modal states
  const [isOpen, setIsOpen] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [detail, setDetail] = useState<any>({});

  const [popUpContent, setPopUpContent] = useState<JSX.Element | null>(null);

  useEffect(() => {
    if (accessibility) {
      try {
        const { add, view, edit, approve, reject, cancel } =
          accessibility["Master"];
        setAllowedActions({ add, view, edit, approve, reject, cancel });
      } catch (error) {
        console.log(error);
      }
    }
  }, [accessibility]);

  const navigate = useNavigate();

  const handleDelete = (taxId: any) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteTaxById(taxId);
      }
    });
  };

  const deleteTaxById = async (id: any) => {
    try {
      const { status, message } = await taxMasterApis.deleteTaxById(id);
      if (status) {
        await Swal.fire("Deleted!", message, "success");
        refreshData();
      } else {
        await Swal.fire("Error", message, "error");
      }
    } catch (error: any) {
      await Swal.fire("Error", error.message, "error");
    }
  };

  const refreshData = () => {
    setRefreshToggle((prev) => !prev);
  };

  const loadDetail = async (taxId: any) => {
    try {
      const response = await taxMasterApis.fetchTaxById(taxId);
      // console.log(response, "response of tax");

      if (response && response.status) {
        setDetail(response.data);
      } else {
        console.error("Failed to fetch tax details");
        setDetail({ taxId });
      }
    } catch (error) {
      console.error("Error fetching tax details:", error);
      setDetail({ taxId });
    }
  };

  const handleModelPopUp = (id?: any, type?: string) => {
    const content = (
      <TaxMasterForm
        formAction={type}
        id={id}
        toggleModelPopUp={() => {
          setIsOpen(false);
          // refreshData();
        }}
        refreshData={refreshData}
      />
    );
    setPopUpContent(content);
    setIsOpen(true);
  };

  const columns = [
    {
      title: "Tax ID",
      dataIndex: "taxSequenceId",
      key: "taxSequenceId",
      width: 100,
    },
    {
      title: "Tax Type",
      dataIndex: "taxType",
      key: "taxType",
      width: 150,
    },
    {
      title: "Tax Name",
      dataIndex: "taxName",
      key: "taxName",
      width: 200,
    },
    {
      title: "Tax Rate",
      dataIndex: "taxRate",
      key: "taxRate",
      width: 150,
      render: (text: any) => `${text}%`,
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 150,
      render: (text: any) => <span>{dayjs(text).format("DD-MMM-YYYY")}</span>,
    },
    {
      title: "Modified Date",
      dataIndex: "modifiedDate",
      key: "modifiedDate",
      width: 150,
      render: (text: any) =>
        text ? (
          <span>{dayjs(text).format("DD-MMM-YYYY")}</span>
        ) : (
          <span>N/A</span>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (text: any) => {
        const normalizedStatus = normalizeStatus(text);
        return (
          <span
            className={`tb-status text-${normalizedStatus === "Active" ? "success" : "danger"
              }`}
          >
            {normalizedStatus}
          </span>
        );
      },
    },
    {
      title: "Details",
      dataIndex: "action",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text: any, record: any) => {
        const normalizedStatus = normalizeStatus(record.status);
        const canView = allowedActions?.view;
        const canEdit =
          allowedActions?.edit && normalizeStatus(record.status) === "Active";
        const canToggleStatus = true;
        const canDelete = allowedActions?.cancel;
        const isActive = normalizedStatus === "Active";

        const availableActions = [
          canView,
          canEdit,
          canToggleStatus,
          canDelete,
        ].filter(Boolean).length;

        return (
          <div
            className="details-cell"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "40px",
              gap: "8px",
            }}
          >
            {canEdit && (
              <div className="edit-hover-wrapper">
                <a
                  href="#edit"
                  className="btn btn-icon btn-trigger edit-hover-icon"
                  id={`edit-${record.taxId}`}
                  onClick={(ev) => {
                    ev.preventDefault();
                    handleModelPopUp(record.taxId, "edit");
                  }}
                >
                  <Icon name="edit-alt-fill"></Icon>
                </a>

                <TooltipComponent
                  id={`edit-tooltip-${record.taxId}`}
                  direction="top"
                  icon=""
                  text="Edit"
                  containerClassName=""
                  target={`edit-${record.taxId}`}
                  iconClass={undefined}
                />
              </div>
            )}

            {(canView || canEdit || canToggleStatus || canDelete) && (
              <UncontrolledDropdown direction="start">
                <DropdownToggle
                  tag="button"
                  className="btn btn-sm btn-icon btn-light"
                  style={{
                    border: "none",
                    background: "none",
                    position: "relative",
                    zIndex: 1,
                    color: "#526484",
                  }}
                >
                  <Icon name="more-h" style={{ color: "inherit" }}></Icon>
                </DropdownToggle>
                <DropdownMenu
                  end
                  style={{
                    position: "fixed",
                    minWidth: "160px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    border: "1px solid #e5e5e5",
                    zIndex: 9999,
                    inset: "auto auto auto auto !important",
                  }}
                  container="body"
                >
                  <ul className="link-list-opt no-bdr" style={{ margin: 0 }}>
                    {/* view option */}
                    {canView && (
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#view"
                          onClick={async (ev) => {
                            ev.preventDefault();
                            await loadDetail(record.taxId);
                            setViewModal(true);
                          }}
                          style={{
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Icon name="focus" className="me-2" />
                          <span>View Details</span>
                        </DropdownItem>
                      </li>
                    )}

                    {/* Edit option - only for active records */}
                    {canEdit && (
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#edit"
                          onClick={(ev) => {
                            ev.preventDefault();
                            handleModelPopUp(record.taxId, "edit");
                          }}
                          style={{
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                            textDecoration: "none",
                          }}
                        >
                          <Icon name="edit" className="me-2"></Icon>
                          <span>Edit</span>
                        </DropdownItem>
                      </li>
                    )}

                    {/* Delete option */}
                    {canDelete && (
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#delete"
                          onClick={(ev) => {
                            ev.preventDefault();
                            handleDelete(record.taxId);
                          }}
                          style={{
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                            textDecoration: "none",
                          }}
                        >
                          <Icon name="trash" className="me-2"></Icon>
                          <span>Delete</span>
                        </DropdownItem>
                      </li>
                    )}
                  </ul>
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="taxmaster-list"
        title="Tax Master"
        columns={columns}
        extra={{
          tableScrollX: 1200,
        }}
        refresh={refreshToggle}
        topRightJSX={
          allowedActions?.add ? (
            <Button
              color="primary"
              onClick={() => handleModelPopUp(null, "add")}
            >
              <Icon name="plus"></Icon>
              <span>Add</span>
            </Button>
          ) : (
            <></>
          )
        }
      />

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isOpen}
        toggle={() => setIsOpen(false)}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
      >
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            setIsOpen(false);
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>
        <ModalBody>{popUpContent}</ModalBody>
      </Modal>

      {/* View Details Modal */}
      <Modal
        isOpen={viewModal}
        toggle={() => setViewModal(false)}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
      >
        <ModalBody>
          <a
            href="#cancel"
            onClick={(ev) => {
              ev.preventDefault();
              setViewModal(false);
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="nk-modal-head">
            <h4 className="nk-modal-title title">Tax Details</h4>
          </div>
          <div className="nk-tnx-details mt-sm-3">
            <Row className="gy-3">
              <Col lg={6}>
                <span className="sub-text">Tax ID</span>
                <span className="caption-text">{detail.taxSequenceId}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Tax Name</span>
                <span className="caption-text">{detail.taxName}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Tax Rate</span>
                <span className="caption-text">{detail.taxRate}%</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Tax Type</span>
                <span className="caption-text">{detail.taxType}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Default Tax</span>
                <span className="caption-text">
                  {detail.isDefault ? "Yes" : "No"}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Status</span>
                <Badge
                  color={
                    normalizeStatus(detail.status) === "Active"
                      ? "success"
                      : "danger"
                  }
                  size="md"
                >
                  {detail.status}
                </Badge>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Created Date</span>
                <span className="caption-text">
                  {detail.createdDate ? FormatDate(detail.createdDate) : "N/A"}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Modified Date</span>
                <span className="caption-text">
                  {detail.modifiedDate
                    ? FormatDate(detail.modifiedDate)
                    : "N/A"}
                </span>
              </Col>
            </Row>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default TaxMaster;
